import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { seoHead } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { submitContact } from "@/lib/contacts.functions";
import { getRecaptchaToken, isRecaptchaEnabled } from "@/lib/recaptcha";

export const Route = createFileRoute("/contact")({
  head: () =>
    seoHead({
      title: "お問い合わせ - KAGEM",
      description:
        "KAGEMへのお問い合わせフォーム。ご質問、機能のご要望、不具合の報告、取材・提携のご相談などを受け付けています。担当者より順次ご返信いたします。",
      path: "/contact",
    }),
  component: ContactPage,
});

const FormSchema = z.object({
  name: z.string().trim().min(1, "お名前を入力してください").max(100),
  email: z.string().trim().email("有効なメールアドレスを入力してください").max(255),
  subject: z.string().trim().min(1, "件名を入力してください").max(200),
  body: z
    .string()
    .trim()
    .min(10, "本文は10文字以上で入力してください")
    .max(5000, "本文は5000文字以内で入力してください"),
});

function ContactPage() {
  const submitFn = useServerFn(submitContact);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = FormSchema.safeParse({ name, email, subject, body });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "入力内容を確認してください");
      return;
    }
    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken("contact_submit");
      const { data: sessionData } = await supabase.auth.getSession();
      const headers = sessionData.session
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined;
      await submitFn({
        data: { ...parsed.data, recaptcha_token: recaptcha_token ?? undefined },
        headers,
      });
      setSubmitted(true);
      toast.success("お問い合わせを受け付けました");
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("recaptcha") || msg.includes("RECAPTCHA")) {
        toast.error("送信内容の検証に失敗しました。ページを再読み込みして再度お試しください。");
      } else {
        toast.error("送信に失敗しました。しばらくしてから再度お試しください。");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" />
            トップへ戻る
          </Link>
        </Button>

        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">お問い合わせ</h1>
          </div>

          {submitted ? (
            <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
              <h2 className="mt-3 text-lg font-semibold">お問い合わせを受け付けました</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                内容を確認の上、通常2〜3営業日以内に返信いたします。
              </p>
              <Button asChild variant="outline" className="mt-5">
                <Link to="/">トップへ戻る</Link>
              </Button>
            </div>
          ) : (
            <>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                ご質問・ご要望・トラブル報告などはこちらからお送りください。
                通常2〜3営業日以内に返信いたします。
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="contact-name">お名前</Label>
                  <Input
                    id="contact-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">メールアドレス</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-subject">件名</Label>
                  <Input
                    id="contact-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-body">お問い合わせ内容</Label>
                  <Textarea
                    id="contact-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={5000}
                    required
                    className="mt-1 min-h-[160px]"
                    placeholder="不具合・課金・コンテンツ通報など、できるだけ詳しくご記入ください。"
                  />
                  <p className="mt-1 text-right text-xs text-muted-foreground">
                    {body.length} / 5000
                  </p>
                </div>

                <Button type="submit" disabled={busy} className="w-full">
                  {busy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      送信中…
                    </>
                  ) : (
                    "送信する"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  送信前に内容をご確認ください。コンテンツの通報は{" "}
                  <Link to="/report" className="underline hover:text-foreground">
                    通報ページ
                  </Link>{" "}
                  からも受け付けています。
                </p>
                {isRecaptchaEnabled() && (
                  <p className="text-[10px] leading-relaxed text-muted-foreground">
                    このサイトは reCAPTCHA によって保護されており、Google の{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      プライバシーポリシー
                    </a>{" "}
                    と{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      利用規約
                    </a>
                    が適用されます。
                  </p>
                )}
              </form>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
